import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { PassActions } from "../components/memberpass/PassActions";
import { PassPhoto } from "../components/memberpass/PassPhoto";
import { beltHex, beltTextColor } from "../lib/beltColor";
import { injectPassManifest, registerPassServiceWorker } from "../components/memberpass/installPass";

const API = process.env.REACT_APP_BACKEND_URL;
const QR_ID = "member-pass-qr-canvas";

export default function MemberPass() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [offline, setOffline] = useState(false);
  const cacheKey = `member_pass_${token}`;

  useEffect(() => {
    registerPassServiceWorker();
    axios.get(`${API}/api/public/member-pass/${token}`)
      .then((r) => { setData(r.data); localStorage.setItem(cacheKey, JSON.stringify(r.data)); })
      .catch((e) => {
        const cached = localStorage.getItem(cacheKey);
        if (cached && !e.response) { setData(JSON.parse(cached)); setOffline(true); return; }
        setErr(e.response?.data?.detail || "Carte introuvable ou lien expiré");
      });
  }, [token, cacheKey]);

  useEffect(() => { if (data?.name) injectPassManifest(data.name); }, [data?.name]);

  if (err) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 flex items-center justify-center p-6" data-testid="member-pass-error">
      <div className="text-center">
        <div className="text-3xl mb-2">🔒</div>
        <div className="text-lg">{err}</div>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center" data-testid="member-pass-loading">
      Chargement de la carte…
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black text-zinc-100 flex flex-col items-center px-5 py-10" data-testid="member-pass-view">
      <div className="text-xs uppercase tracking-widest text-amber-500/80">{data.branding?.school}</div>
      <div className="text-[11px] text-zinc-500 mb-6">{data.branding?.tagline}</div>
      {offline && (
        <div className="mb-4 text-[11px] px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300" data-testid="pass-offline-badge">
          📴 Mode hors ligne — carte enregistrée sur cet appareil
        </div>
      )}

      <div className="w-full max-w-sm rounded-2xl bg-gradient-to-b from-zinc-900 to-zinc-950 border border-amber-900/40 p-6 shadow-2xl overflow-hidden relative">
        {data.belt && <div className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: beltHex(data.belt) }} data-testid="pass-belt-band" />}
        <PassPhoto token={token} data={data} onChange={(url) => {
          const next = { ...data, photo_url: url };
          setData(next); localStorage.setItem(cacheKey, JSON.stringify(next));
        }} />
        <h1 className="text-xl font-bold text-amber-300 text-center" data-testid="pass-name">{data.name}</h1>
        <div className="flex items-center justify-center gap-2 mt-1 mb-5 flex-wrap">
          {data.emblem_url && <img src={data.emblem_url} alt={data.emblem_label || data.discipline} className="h-9 w-9 object-contain" data-testid="pass-emblem" />}
          <span className="text-sm text-zinc-400 capitalize">{data.discipline}</span>
          {data.belt && (
            <span data-testid="pass-belt" className="text-[11px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full border border-white/20"
              style={{ backgroundColor: beltHex(data.belt), color: beltTextColor(data.belt) }}>
              Ceinture {data.belt}
            </span>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 flex items-center justify-center mb-5" data-testid="pass-qr">
          <QRCodeCanvas id={QR_ID} value={data.qr_value} size={210} level="M" includeMargin />
        </div>

        {data.pin && (
          <div className="text-center" data-testid="pass-pin">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Code PIN de secours</div>
            <div className="text-3xl font-mono font-bold text-amber-200 tracking-[0.3em]">{data.pin}</div>
          </div>
        )}
      </div>

      <PassActions data={data} qrCanvasId={QR_ID} />

      <div className="mt-6 max-w-sm text-center text-xs text-zinc-400">
        Présente ce QR code à la tablette d'accueil du dojo pour enregistrer ta présence.
        Oublié ton téléphone ? Donne ton code PIN ou ton nom à l'accueil.
      </div>

      <div className="mt-8 text-[10px] text-zinc-600 text-center">
        © Institut Impact Élite Martial · Kru Ralph D. Jean-Pierre
      </div>
    </div>
  );
}
